import { useCallback, useEffect, useState } from "react";

/**
 * Wraps any analytics.api.js endpoint and exposes its loading/value/error state.
 *
 * @param {(params: any) => Promise<any>} apiFn  — an export from services/analytics.api.js
 * @param {any} [params]     — params object passed to apiFn (e.g. { from, to })
 * @param {{ enabled?: boolean }} [options]  — set enabled: false to skip fetching (e.g. until params are ready)
 *
 * @returns {{ value: any, loading: boolean, error: any, refetch: () => Promise<void> }}
 *
 * Usage:
 *   const { value: paymentAnalytics, loading } = useAnalytics(getPaymentRequestAnalytics, { from, to });
 *   const { value: txnAnalytics, loading } = useAnalytics(getTransactionAnalytics, { from, to }, { enabled: !!userId });
 */
export default function useAnalytics(apiFn, params, { enabled = true } = {}) {
  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFn(params);
      setValue(res?.data ?? null);
    } catch (err) {
      setError(err);
      setValue(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFn, enabled, JSON.stringify(params)]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { value, loading, error, refetch };
}
